import { useI18n } from 'vue-i18n'
import { currentCountry } from '$services/shopify'

declare module 'vue' {
    interface ComponentCustomProperties {
        $cms: CMS
    }
}

export namespace CMS {
    export interface InitOptions {
        baseUrl: string
        apiToken?: string
    }

    export interface FetchOptions {
        withMeta?: boolean
        locale?: string
        countryCode?: string
        sort?: string
        fields?: string[]
        populate?: string[] | string
        filters?: Record<string, string|number>
        ids?: boolean
        pagination?: {
            withCount?: boolean
            page?: number
            pageSize?: number
            start?: number
            limit?: number
        }
    }

    export interface MetaResponse<T> {
        meta: CMS.Meta
        data: T
    }

    // Responses

    export interface Data<T> {
        id: number
        attributes: T
    }

    export interface Meta {
        pagination: {
            page: number
            pageSize: number
            pageCount: number
            total: number
        }
    }

    interface Error {
        status: number
        name: string
        message: string
        details: Record<string, any>
    }

    interface ContentBase {
        meta?: Meta
        error?: Error
    }

    export interface Content<T> extends ContentBase {
        data?: Data<T>
    }

    export interface ContentArray<T> extends ContentBase {
        data?: Array<Data<T>>
    }

    export interface RequestOptions {
        headers?: {
            Authorization?: string
        }
    }
}

class CMS {
    private baseUrl: string
    private requestOptions: CMS.RequestOptions = {}

    constructor(options: CMS.InitOptions) {
        this.baseUrl = options.baseUrl

        if (options.apiToken) {
            this.requestOptions.headers = {
                Authorization: `Bearer ${options.apiToken}`
            }
        }
    }

    // eslint-disable-next-line complexity
    private async makeRequest(path: string, options: CMS.FetchOptions) {
        let locale = options.locale
        let countryCode = options.countryCode

        if (!options.locale) {
            // locale = I18n.locale
            throw new Error('No locale provided for fetch request!')
        }

        if (!options.countryCode) {
            countryCode = 'US'
        }

        // convert i18n locale name to Strapi locale name
        if (locale === 'es-XL') {
            locale = 'es-419'
        }
        if (locale === 'pt-br') {
            locale = 'pt-BR'
        }

        let url = `${this.baseUrl}/${path}?locale=${locale}&countryCode=${countryCode}`

        if (options.sort) url += `&sort=${options.sort}`
        if (options.fields) url += this.arrayToRequest('fields', options.fields)

        if (options.populate) {
            Array.isArray(options.populate)
                ? url += this.arrayToRequest('populate', options.populate)
                : url += `&populate=${options.populate}`
        }

        if (options.filters) {
            Object.keys(options.filters).forEach((key) => {
                url += `&filters[${key}]=${encodeURIComponent(options.filters[key])}`
            })
        }

        if (options.pagination) {
            Object.keys(options.pagination).forEach((key) => { url += `&pagination[${key}]=${options.pagination[key as keyof CMS.FetchOptions['pagination']]}` })
        }

        return $fetch(url, this.requestOptions)
    }

    private arrayToRequest(type: string, items: Array<string>) {
        let str = ''
        items.forEach((val, i) => { str += `&${type}[${i}]=${val}` })
        return str
    }

    async fetchOne<T>(path: string, slug: string | null, options?: CMS.FetchOptions & { withMeta?: false }): Promise<T>
    async fetchOne<T>(path: string, slug: string | null, options: CMS.FetchOptions & { withMeta: true }): Promise<CMS.MetaResponse<T>>
    // eslint-disable-next-line complexity
    async fetchOne<T>(path: string, slug: string | null, options?: CMS.FetchOptions) {
        if (!options) options = {}
        if (!options.filters) options.filters = {}
        if (slug) options.filters.slug = slug
        if (!options.countryCode) options.countryCode = currentCountry.code
        if (!options.locale) options.locale = useI18n().locale.value

        const json = await this.makeRequest(path, options) as CMS.Content<T>
        if (json.error) throw new Error(json.error.message)
        if (!json.data) throw new Error('No data')
        if (Array.isArray(json.data) && !json.data.length) throw new Error('No data')

        const data = Array.isArray(json.data) ? json.data[0].attributes : json.data.attributes
        if (options.ids && data) {
            data.id = Array.isArray(json.data) ? json.data[0].id : json.data.id
        }

        return options.withMeta ? { meta: json.meta, data } : data
    }

    async fetchMany<T extends any[]>(path: string, options?: CMS.FetchOptions & { withMeta?: false }): Promise<T>
    async fetchMany<T extends any[]>(path: string, options: CMS.FetchOptions & { withMeta: true }): Promise<CMS.MetaResponse<T>>
    async fetchMany<T extends any[]>(path: string, options?: CMS.FetchOptions) {
        if (!options) options = {}
        if (!options.pagination) options.pagination = { limit: 100 }

        const json = await this.makeRequest(path, options) as CMS.Content<T>
        if (json.error) throw new Error(json.error.message)
        if (!json.data) throw new Error('No data')
        if (!Array.isArray(json.data)) throw new Error('Not a many request')

        const data = json.data.map((item) => item.attributes)

        return options.withMeta ? { meta: json.meta, data } : data
    }
}

export default defineNuxtPlugin((_nuxtApp) => {
    const runtimeConfig = useRuntimeConfig()
    const cmsProvider = new CMS(<CMS.InitOptions>runtimeConfig.public.cms)
    return {
        provide: {
            cms: cmsProvider
        }
    }
})
